import { toCamel } from '@/components/utils/core/word';
import {
  SEARCH_CATEGORY_SLUGS_MAP,
  CONTENT_CATEGORY_SLUGS,
  IMAGE_GENERATOR_BASE_URL,
} from '@/components/common/UniversalSearch/constants';
import { Author, CompanyPagesCatalog, CompanyPagesCatalogPageType, SearchResultCategory } from '@/types/graphql';
import type { UniversalSearchResultItem, SharingImageType } from '@/components/common/UniversalSearch/types';
import type { UserAuthor } from '@/types/graphql';
import markupParser, { removeTags } from './markupParser';

type ItemData = {
  id: string;
  slug: string;
  category: string;
  link: string;
  isExternal?: boolean;
  section?: SearchResultCategory;
};

export const buildSearchItemData = (item: UniversalSearchResultItem): ItemData | undefined => {
  switch (item.__typename) {
    case 'AcademyArticleSearchResult':
      return {
        id: String(item.article.id),
        slug: item.article.slug,
        category: item.category,
        link: `/academy/${item.article.slug}`,
        section: SearchResultCategory.AcademyArticles,
      };
    case 'CompanySearchResult':
      return {
        id: item.company.id,
        slug: item.company.slug ?? '',
        category: item.category,
        link: `/salaries/company/${item.company.slug}/`,
        isExternal: true,
        section: SearchResultCategory.Companies,
      };
    case 'PortfolioUserSearchResult':
      return {
        id: item.portfolioUser.slug,
        slug: item.portfolioUser.slug,
        category: item.category,
        link: `/p/${item.portfolioUser.slug}`,
        section: SearchResultCategory.UserPortfolios,
      };
    case 'WarRoomPostSearchResult':
      return {
        id: item.post.id,
        slug: item.post.slug,
        category: item.category,
        link: `/war-room/posts/${item.post.slug}`,
        section: SearchResultCategory.WrPosts,
      };
    case 'WarRoomUserSearchResult':
      return {
        id: String(item.warRoomUser?.id),
        slug: String(item.warRoomUser?.username),
        category: item.category,
        link: `/war-room/profile/${item.warRoomUser?.username}`,
        section: SearchResultCategory.WrUsers,
      };
    case 'WarRoomPostCommentSearchResult':
      return {
        id: String(item.record?.id),
        slug: String(item.record?.id),
        category: item.category,
        link: commentLink(item.record?.post?.slug, item.record?.id),
        section: SearchResultCategory.WrComments,
      };
    case 'CompanyPagesCatalogSearchResult':
      const link =
        item.page.pageType === CompanyPagesCatalogPageType.Compensation
          ? compensationPagesLink(item.page)
          : companyPagesLink(item.page);
      return {
        id: String(item.page?.id),
        slug: String(item.page?.id),
        category: item.category,
        link: link,
        section: SearchResultCategory.CompanyPagesCatalogs,
      };
  }
};

export const commentLink = (slug: string, id: string) => `/war-room/posts/${slug}/comments/${id}`;

export const companyPagesLink = (companyPagesCatalog: CompanyPagesCatalog) => {
  const pageType = companyPagesCatalog.pageType;
  const company = companyPagesCatalog.company?.slug;
  const role = companyPagesCatalog.roleCategory?.slug;
  const segments = ['salaries'];

  if (company) {
    segments.push(`company/${company}`);
  }

  switch (pageType) {
    case CompanyPagesCatalogPageType.Salaries:
      segments.push('salaries');
      if (role) {
        segments.push(role);
      }
      break;
    case CompanyPagesCatalogPageType.Reviews:
      segments.push('reviews');
      break;
    case CompanyPagesCatalogPageType.Interviews:
      segments.push('interviews');
      break;
  }

  const url = segments.join('/');

  return `/${url}/`;
};

export const compensationPagesLink = (companyPagesCatalog: CompanyPagesCatalog) => {
  const role = companyPagesCatalog.roleCategory?.slug;
  const accountSize = companyPagesCatalog.customerSegment?.slug;
  const region = companyPagesCatalog.region?.slug;
  const segments = ['salaries/compensation'];

  if (accountSize) {
    segments.push(`${accountSize}-${role}`);
  } else if (region) {
    segments.push(`${role}/${region}`);
  } else if (role) {
    segments.push(role);
  }

  const url = segments.join('/');

  return `/${url}/`;
};

export const displayCompanyEmployees = (value: string) =>
  value
    .split('-')
    .map(count => {
      if (count.length >= 4) {
        // Transform 1000 to 1k and 1000+ to 1k+
        return count.endsWith('+') ? `${count.slice(0, -4)}k+` : `${count.slice(0, -3)}k`;
      }
      return count;
    })
    .join('-');

export const getAuthorDetails = (author: UserAuthor | Author) => {
  let initials = '';
  let username = '';
  let avatar = '';
  let isAnonymous = false;
  let link = '';
  let isPublic = true;

  if (author.__typename === 'Public') {
    initials = author.firstname?.substring(0, 2) ?? '';
    avatar = String(author.avatarUrl);
    username = `${author.firstname} ${author.lastname}`.trim();
    link = `/p/${author.slug}`;
    isPublic = Boolean(author.portfolioIsOpened);
  } else if (author.__typename === 'Anonymous') {
    initials = author.username?.substring(0, 2) ?? '';
    avatar = String(author.warRoomAvatarUrl);
    username = author.username ?? '';
    isAnonymous = true;
    link = `/war-room/profile/${username}`;
  } else if (author.__typename === 'Author') {
    username = author.name ?? '';
    initials = username.substring(0, 2) ?? '';
    avatar = String(author.avatar?.url);
    link = author.linkUrl ?? '';
    isAnonymous = true;
  }

  return { initials, username, avatar, isAnonymous, link, isPublic };
};

export const getSlugBySearchCategory = (category: SearchResultCategory) => SEARCH_CATEGORY_SLUGS_MAP.get(category);

// remove starting of html tag if it doesn't close to prevent < & </ in text during the streaming
const fixHtml = (text: string): string => text.replace(/<[^>]*$/g, '');

export const textSteamToHtml = (text: string): string => {
  return markupParser(removeTags(fixHtml(text)));
};

export const downloadScreenshotPath = (
  sid: string,
  type: SharingImageType,
  env: string | undefined = process.env.ROLLBAR_ENV,
) => {
  const environment = env?.replace(/^prod$/, 'production') ?? 'production';
  return `${IMAGE_GENERATOR_BASE_URL}?sid=${sid}&type=${type}&env=${environment}`;
};

/**
 * String param for window.open
 */
export const getPopupCenteredPosition = (width: number, height: number) => {
  const top = (screen?.height - height) / 2;
  const left = (screen?.width - width) / 2;
  return `width=${width}, height=${height}, top=${top}, left=${left}`;
};

export const buildSgptPostCreationLink = (sid: string, category: string, type: string = '', query = '') =>
  `/war-room/posts/new?category=${category}&sgpt_sid=${sid}&sgpt_type=${type}${
    query ? `&query=${encodeURIComponent(query)}` : ''
  }`;
